import { GenericAbortSignal } from 'axios';
import { ActionState, EPDStatusDisplayName, FileModel, FileModelPlus, PCRModel, VerificationType } from 'services/EpdClient';
import { EPDDeclareContentProductModel } from './content-declaration/EPDDeclareContentProductModel';
import { ToolStandardEpdOptionModel } from './tools/ToolStandardEpdOptionModel';
import { ToolVersionStandardEpdOptionModel } from './tools/ToolVersionStandardEpdOptionModel';
import { ToolCompilerOptionModel } from './tools/ToolCompilerOptionModel';

// utility type to make all properties of T nullable
type Nullable<T> = { [K in keyof T]: T[K] | null };

export type BaseQueryParams = {
  signal: GenericAbortSignal;
};

export type Option = {
  label: string;
  value: string;
  isDisabled?: boolean;
};

export type UserOptionModel = Option & {
  userName: string;
};

export type OptionNameCode = {
  name: string;
  code: string;
};

export type LCAModules<T> = {
  a1?: T;
  a2?: T;
  a3?: T;
  a1A2A3?: T;
  a4?: T;
  a5?: T;
  b1?: T;
  b2?: T;
  b3?: T;
  b4?: T;
  b5?: T;
  b6?: T;
  b7?: T;
  c1?: T;
  c2?: T;
  c3?: T;
  c4?: T;
  d?: T;
  variation?: T;
};

export type LcaResultsViewModel = {
  lcaResults: LcaResultModel[];
};

export type LcaResultModel = LCAModules<string> & {
  id?: string | null;
  epdVersionId: string | null;
  indicatorGroupCode?: string;
  ord: number;
  indicatorId?: string;
  indicatorName?: string;
  impactCategory?: string;
  unit?: string;
};

export type LcaCustomResultModel = IVersionedEntity & {
  resultType: string;
  bestCaseProduct: string;
  worstCaseProduct: string;
  scenarioId?: string;
  scenarioName?: string;
  scenarioDescription: string;
  gridAcronyms: string;
  gridDisclaimers: string;
};

export type LcaCustomResultGridDataModel = LCAModules<string> & {
  id?: string | null;
  lcaCustomResultId: string;
  ord: number;
  indicatorName?: string;
  impactCategory?: string;
  unit?: string;
};

export interface LCAResultAdditionalVoluntaryAcronymsModel {
  acronyms: string | null;
}

export type CpcrPerformanceTableModel = {
  ord: number;
  tableCode: string;
  tableTitle: string;
  isFixed: boolean;
  tableDescription?: string;
};

export type CpcrPerformanceInformationModel = {
  tables: CpcrPerformanceTableModel[];
  columns: any[];
  data: CpcrPerformanceTableDataModel[];
};

export type CpcrPerformanceTableDataModel = {
  id?: string | null;
  tableCode: string;
  ord: number;
  a1?: string | null;
  a1Placeholder?: string | null;
  a2?: string | null;
  a2Placeholder?: string | null;
  a3?: string | null;
  a3Placeholder?: string | null;
  a4?: string | null;
  a5?: string | null;
  a6?: string | null;
  a7?: string | null;
  a8?: string | null;
  a9?: string | null;
  a10?: string | null;
  a11?: string | null;
  a12?: string | null;
  a13?: string | null;
  a14?: string | null;
  a15?: string | null;
  rowClassName?: string | null;
};

export type ElectricityDataModel = {
  id?: string;
  epdVersionId?: string;
  typeOfElectricityMix?: number;
  climateImpact?: number;
  energySources?: EnergySourceModel[];
  residualElectricityMixCalculationMethod?: string;
  electricityUsedInProcessName?: string;
  ord: number;
  isDefault: boolean;
};

export type EnergySourceModel = {
  id?: string;
  source?: string;
  value?: number | undefined;
};

export type DeclarationOfCO2AssumptionModel = {
  assumptions?: string;
  isSelected: boolean;
};

export type DeclarationOfCO2AssumptionModelWithParams = {
  epdVersionId: string;
  nameId: string;
  assumptions?: string;
  isSelected: boolean;
};

export type DeclarationOfCO2Model = {
  epdVersionId?: string;
  nameId: string;
  name?: string;
  assumptions?: string;
  isSelected: boolean;
};

export type CO2UptakeAssociationModelWithParams = {
  epdVersionId: string;
  id?: string;
  association?: string;
};

export type CO2UptakeAssociationModel = {
  id?: string;
  association?: string;
};

export type ScrapInputModel = {
  id?: string;
  epdVersionId?: string;
  name?: string;
  value?: number;
  ord: number;
};

export type ShareOfTotalScrapInputModel = {
  id?: string;
  epdVersionId?: string;
  sharePercent?: number;
};

export type ScenarioModuleReadModel = {
  defaultScenarioModuleExplanatoryName?: string;
  defaultScenarioModuleBriefDescription?: string;
  defaultScenarioModuleDescription?: string;
  scenarioModuleItems: ScenarioModuleItemModel[];
};

export type ScenarioItemModel = IVersionedEntity & {
  name?: string;
  description?: string;
  value?: string;
  isDefaultScenario: boolean;
  enabledRSL?: boolean;
};

export type SystemBoundaryModel = IVersionedEntity & {
  shareOfSpecificData?: number;
  variationProducts?: number;
  variationSites?: number;
  description?: number;
  hasExcludedModules?: boolean;
  excludedModulesJustification?: string;
  processFlowDiagramsDescription?: string;
  processFlowDiagramsImages: FileModelPlus[];
};

export type SystemBoundaryGridModel = {
  epdVersionId: string;
  declaredModules: LCAModules<boolean | undefined | null> | null;
  declaredModulesGeography: LCAModules<string> | null;
};

export type UpdateDeclaredModulesModel = {
  epdVersionId: string;
  fieldName: string;
  value: boolean | null;
};

export type UpdateDeclaredModulesGeographyModel = {
  epdVersionId: string;
  fieldName: string;
  value: string | null;
};

export type ScenarioModuleItemModel = {
  id?: string | null;
  scenarioId: string;
  module: string;
  ord: number;
  description?: string | undefined | null;
  value?: string | undefined | null;
  unitId?: string | undefined | null;
  placeholder?: string | undefined | null;
  unitRequired?: boolean | undefined | null;
  mandatory?: boolean | undefined | null;
};

export type ScenarioModuleRequest = {
  epdVersionId: string;
  scenarioId: string;
  module: string;
};

export type ScenarioModulesRequest = {
  epdVersionId: string;
  scenarioId: string;
  modules: string[];
};

export type ScenarioModuleDefaultDescriptionModel = ScenarioModuleRequest & {
  defaultScenarioModuleExplanatoryName: string;
  defaultScenarioModuleBriefDescription: string;
  defaultDescription: string;
};

export type TransportationPerformanceModel = {
  id?: string;
  epdVersionId?: string;
  transportationAverageCarLoad?: string;
  transportationPerformance?: string;
  transportationServiceLifeDistance?: string;
};

export type DictionaryValue = {
  id: string;
  dictionaryCode: string;
  key: number;
  value: string;
  code: string;
  description: string;
  ord: number;
};

export type DictionaryCatalogItem = {
  id?: string;
  name: string;
  code: string;
  description: string;
};

export type DictionaryValues = DictionaryValue[];
export type DictionaryCatalog = DictionaryCatalogItem[];
export type LCASpecificationModel = {
  id: string;
  epdVersionId?: string;
  shouldDeclarationsBeFetched: boolean;
  shouldTransportationPerformanceBeFetched: boolean;
  epdBasedOnUnit?: EPDBasedOnUnitEnum;
  functionalUnitDescription?: string;
  scrapInputsContributionLevel?: ScrapInputsContributionLevelEnum;
  lcaSoftwareId?: string;
  lcaSoftwareVersionId?: string;
  lcaBasedDescription?: string;
  technologyDescription?: string;
  otherSoftwareName?: string;
  otherSoftwareVersion?: string;
  justificationForInclusion?: string;
};

export type CollocationCenterModel = Nullable<{
  id: string;
  name: string;
  description: string;
  logotypeUrl: string;
  countryCodes: string[];
}>;

export type UpdateCollocationCenterModel = {
  id: string | null;
  name: string | null;
  description: string | null;
  logotypeUrl: string | null;
  countryCodes: string[];
  removeLogo: boolean;
};

export type InfrastructureAndCapitalGoodsModel = {
  includedInProcesses?: string;
  contributionLevel?: string;
  description?: string;
};

export type ReferenceFlowModel = {
  epdVersionId?: string;
  productFlowName: string;
  unitId: string;
  value: number;
  errors: any;
};

export type ConversionFactorModel = {
  epdVersionId?: string;
  unitId: string;
  value: number | undefined;
  errors: any;
};

export type EPDDataSourceModel = {
  id: string;
  epdVersionId: string;
  dataSourceId?: string;
  dataSourceVersionId?: string;
  isMain: boolean;
};

export type DataSourceOptionModel = {
  id: string;
  name?: string;
  isDisabled: boolean;
  dataSourceVersions: Option[];
};

export type ReferencePackageVersion15804 = {
  id: string;
  name: string;
};

export type LCASoftwareOptionModel = {
  id: string;
  name?: string;
  versionRequired?: boolean;
  lcaSoftwareVersions: Option[];
};

export enum EPDProductType {
  Goods = 'Goods',
  Services = 'Services',
}

export enum EPDBasedOnUnitEnum {
  DeclaredUnit = 'DeclaredUnit',
  FunctionalUnit = 'FunctionalUnit',
}

export enum ScrapInputsContributionLevelEnum {
  MorePermissiblePercent = 'MorePermissiblePercent',
  LessPermissiblePercent = 'LessPermissiblePercent',
}

export enum ProductStatusEnum {
  OnTheMarket = 'OnTheMarket',
  ForthcomingOrNotYetOnTheMarket = 'ForthcomingOrNotYetOnTheMarket',
  RecentlyOnTheMarket = 'RecentlyOnTheMarket',
}

export type TagModel = {
  id: string;
  name: string;
};

export interface VerificationViewModel {
  verifierInfo: EPDVerifierModel;
  verificationReports: FileModel[];
  verificationType: VerificationType;
  approvalTimestamp: string | null;
}

export interface CompilerCompositeStatusModel {
  isValid: boolean;
  errors: Record<string, any>;
  fieldsState: Record<string, ActionState>;
  actionsStatus: CompilerActionsStatusModel;
}

export interface CompilerActionsStatusModel {
  isMandatoryComplete: boolean;
  isVerifierInvolved: boolean;
  deleteDraft: ActionState;
  cancelUpdate: ActionState;
  startVerification: ActionState;
  cancelVerification: ActionState;
  sendForPublication: ActionState;
  cancelPublication: ActionState;
  deregister: ActionState;
  unlock: ActionState;
  cancelDeregistration: ActionState;
  approveVerification: ActionState;
  declineVerification: ActionState;
  retractVerification: ActionState;
  publish: ActionState;
  publishTargetVersionId: string | null;
  previewPdf: ActionState;
  downloadPdf: ActionState;
  generateRegistrationCertificate: ActionState;
  declinePublication: ActionState;
  depublishByAdmin: ActionState;
  depublishByCustomer: ActionState;
  sendForPublicationEditorial: ActionState;
  editorialUpdating: ActionState;
  licenseeAdminSendToPublish: ActionState;
  downloadIlcdEpdBundle: ActionState;
  statusDisplayName: string;
  statusMessageCode: string;
  commentToStatus: string | null;
  licenseeAdminReviewComment: string | null;
}

export type CompilerSurveyTrigger = 'StartVerification' | 'SendToPublish';

export type CompilerSurveyResponseAction = 'StartSurvey' | 'Decline';

export type CompilerSurveyInvitationModel = {
  show: boolean;
  surveyUrl: string | null;
};

export type CompilerSurveyResponseModel = {
  action: CompilerSurveyResponseAction;
  dontShowAgain: boolean;
};

export interface IVersionedEntity {
  id: string;
  epdVersionId: string;
}

export enum EPDVerificationFlowType {
  ProcessCertification = 'ProcessCertification',
  ProcessCertificationWithTool = 'ProcessCertificationWithTool',
  IndividualVerification = 'IndividualVerification',
  IndividualVerificationAcb = 'IndividualVerificationAcb',
  IndividualVerificationWithTools = 'IndividualVerificationWithTools',
  IndividualVerificationAcbWithTools = 'IndividualVerificationAcbWithTools',
  FullyPreverifiedTool = 'FullyPreverifiedTool',
  CompilerProcessCertification = 'CompilerProcessCertification',
  CompilerIndividualVerification = 'CompilerIndividualVerification',
  CompilerIndividualVerificationAcb = 'CompilerIndividualVerificationAcb',
}

export type EPDRegistrationCertificateDataModel = {
  companyName: string;
  fullIdentificationNumber: string;
  epdTitle: string;
  verifierName: string;
  verifierType: VerifierType;
  verificationType: VerificationType;
  toolName: string;
  toolVerifierName: string;
  acbOrganizationName: string;
  accreditedByName: string;
  hasPreverifiedTool: boolean;
  hasPreverifiedToolVersion: boolean;
  isEPDCompilerFormat: boolean;
  validityDate?: string | null;
  currentDate: string;
  pcrRegistrationNumber: string;
  verificationFlowType: EPDVerificationFlowType;
};

export type EPDOrganizationInformationModel = IVersionedEntity & {
  name?: string;
  companyName: string;
  contactPersonEmail?: string;
  contactPersonName?: string;
  licenseeName?: string;
  licenseeLogotypeUrl?: string;
  collocationCenterName?: string;
  collocationCenterLogotypeUrl?: string;
  description?: string;
  organizationImages?: FileModelPlus[];
};

export type EPDProductInformationModel = {
  id: string;
  epdVersionId: string;
  productName: string | null;
  productStatus: ProductStatusEnum | null;
  productIdentification: string | null;
  productDescription: string | null;
  productInformationExternal: string | null;
  productionSites: ProductionSiteModel[];
  productGeographicalScopes: EPDProductGeographicalScopeModel[];
  productClassifications: EPDProductClassificationModel[];
  productMaterialProperties: EPDProductMaterialPropertyModel[];
  geographicalScopeDescription: string | null;
  gtin: string | null;
  uncpcId: string | null;
  unitValue: string | null;
  unitType: number | null;
  productDetailsImages: FileModelPlus[];
  technicalPurposeOfProduct: string | null;
  manufacturingOrServiceProvisionDescription: string | null;
  isTheProjectInThePlanningOrDesignPhase: boolean | null;
  isUNCPCCodeNotApplicable: boolean | null;
  okobaudatProductCategoryId: string | null;
  okobaudatProductSubCategoryId: string | null;
  okobaudatProductCategoryTypeId: string | null;
};

export type UpdateEPDProductInformationPropertyModel = {
  productId: string;
  versionId: string;
  propertyName: string;
  newValue: string;
};

export type CreateEPDProductionSiteAddressModel = {
  productId?: string;
  epdVersionId?: string;
};

export type FoundationAddressModel = {
  id: string;
  countryId: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  zipCode: string;
  regionId: string | null;
};

export type ProductionSiteModel = {
  id: string;
  epdVersionId: string;
  countryId: string | null;
  nameOfManufacturer: string;
  name: string;
  addressLine: string;
  addressLineSecond: string;
  city: string;
  zipCode: string;
  regionId: string | null;
};

export type EPDOrganizationInformationAddressModel = IVersionedEntity & {
  address: FoundationAddressModel;
};

export type EPDProductGeographicalScopeModel = {
  id: string;
  name: string;
};

export type EPDProductClassificationModel = {
  id: string;
  productId: string;
  productClassificationType: number | null;
  description: string;
  code: string;
};

export type EPDProductMaterialPropertyModel = {
  id: string;
  productId: string;
  materialPropertyId: string | null;
  value: number | null;
};

export type EPDCoverPageModel = {
  id: string;
  versionId: string;
  coverPageProductImage?: FileModelPlus;
  companyLogoImages?: FileModelPlus[];
  productName: string;
  statementOfConformityWithStandards: string;
};

export type EPDAdditionalInformationModel = IVersionedEntity & {
  references?: string;
  environmental?: string;
  conversion?: string;
  dangerous?: string;
  economic?: string;
  abbreviations?: string;
};

export type ProductCategoryModel = {
  id?: string;
  title?: string | undefined;
  productCategories?: ProductCategoryModel[] | undefined;
};

export type PCRInformationModel = {
  pcr: PCRModel | undefined;
  cpcr: PCRModel | undefined;
  cpcrProductCategory?: ProductCategoryModel | undefined;
};

export type EPDPDFOrganizationInformationAddress = EPDOrganizationInformationAddressModel & {
  countryName: string;
};

export type EPDPDFModel = unknown & {
  generalInformation: any;
  coverPageInformation: any;
  organizationInformation: EPDOrganizationInformationModel & {
    logo: any;
    address: EPDPDFOrganizationInformationAddress;
  };
  products: EPDProductInformationModel[] | undefined;
  companyInformation: any;
  additionalInformation: any;
  pcrInformation: PCRInformationModel;
  countries: any;
  infrastructureAndCapitalGoods: InfrastructureAndCapitalGoodsModel;
  lcaSpecification: LCASpecificationModel;
  contentDeclarationProducts: EPDDeclareContentProductModel[];
  scenarios: ScenarioItemModel[];
  lcaSupportInformation: LcaSupportModel;
  isEPDComplete: boolean;
};

export enum VerifierType {
  Missing = 'Missing',
  Body = 'Accredited Certification Body',
  Individual = 'Approved Individual Verifier',
}

export enum DepublishReasonType {
  DepublishedByAdminEPDNotPaid = 'DepublishedByAdminEPDNotPaid',
  DepublishedByAdminNonCompliant = 'DepublishedByAdminNonCompliant',
}

export type AddressModel = {
  line1: string | null;
  line2: string | null;
  line3: string | null;
  line4: string | null;
  city: string | null;
  country: string | null;
  countryId: string | null;
};

export type VerifierCompanyAddressModel = {
  addressLine: string | null;
  city: string | null;
  zipCode: string | null;
  country: string | null;
};

export type EPDVerifierModel = {
  id: string;
  verifierType: VerifierType;
  contactUserId: string;
  contactName: string;
  companyId: string;
  companyName: string;
  homeCompanyName: string | null;
  address: AddressModel | null;
  companyAddress: VerifierCompanyAddressModel | null;
  website: string | null;
  information: string | null;
  email: string | null;
  phoneNumber: string | null;
  displayName: string | null;
  logotypeFileId: string | null;
  isExternalMembership: string | null;
};

export type EPDDeveloperModel = {
  contactUserId: string;
  contactName: string | null;
  contactEmail: string | null;
  companyId: string;
  companyName: string | null;
  address: AddressModel | null;
  website: string | null;
};

export type LcaSupportModel = {
  epdDevelopers: EPDDeveloperModel[];
};

export type ThirdPartyVerificationModel = {
  epdVerifier: EPDVerifierModel | null;
  preverifiedTool: ToolStandardEpdOptionModel | null;
  preverifiedToolVersion: ToolVersionStandardEpdOptionModel | null;
  preverifiedToolVerifierAccreditedBy: string | null;
  verificationType: VerificationType | null;
  verificationTypeDataSource: VerificationType[];
  processCertificateIssuedBy: EPDVerifierModel | null;
  processCertificates: FileModel[];
  followUpInvolvesThirdPartyVerifier: string | null;
  isCreatedByApiFullyIntegratedPreverifiedTool: boolean;
  preVerifiedToolDataSource: ToolCompilerOptionModel[] | null;
};

export type EPDVerifierCompanyModel = {
  id?: string;
  name?: string | undefined;
  information?: string | undefined;
  verifierType?: VerifierType;
  email?: string | undefined;
  phoneNumber?: string | undefined;
  logotypeFileId?: string | undefined;
  successOperationText?: string | undefined;
  errorText?: string | undefined;
  requestSentAt?: Date | undefined;
};

export type ConnectDeveloperModel = {
  epdId: string;
  membershipId: string;
};

export type RequestCollaborationModel = {
  epdVersionId: string;
  roleId?: string;
  companyId?: string;
  verificationType?: VerificationType;
};

export type AddEPDFileModel = {
  epdId?: string;
  epdProductInformationId?: string;
  propertyName?: string | undefined;
  file?: File | undefined;
  documentTypeId: string | null;
  documentLanguageId: string | null;
};

export type AddEPDPropertyModel = {
  epdId: string;
  versionId: string;
  propertyName: string;
  newValue: any;
};

export type DataQualityAssessmentItemModel = {
  id?: string | null;
  epdVersionId: string;
  ord: number;
  processName?: string;
  sourceType?: string;
  source?: string;
  referenceYear?: string;
  referenceYearStartDate?: string;
  referenceYearEndDate?: string;
  dataCategory?: string;
  shareOfPrimaryData?: string;
};

export type DataQualityAssessmentReadModel = {
  dataQualityAssessmentSummary?: DataQualityAssessmentSummaryModel;
  dataQualityAssessmentItems: DataQualityAssessmentItemModel[];
};

export type DataQualityAssessmentSummaryModel = {
  epdVersionId: string;
  dataQualityAssessmentSummary: string;
  dataQualityAssessmentComment?: string;
};

export enum LibraryQRCodeLogotypeType {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
}

export enum LibraryQRCodeFormat {
  WEB_SIZE_COLOURED = 'WebSizeColoured',
  WEB_SIZE_GRAYSCALE = 'WebSizeGrayscale',
  PRINT_SIZE_COLOURED = 'PrintSizeColoured',
  PRINT_SIZE_GRAYSCALE = 'PrintSizeGrayscale',
}

export enum QRCodeLogotypeType {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
}

export enum AdminLookupKeyEnum {
  COMPANIES = 'companies',
  LICENSEES = 'licensees',
  COUNTRIES = 'countries',
  AGENTS = 'agents',
  AGENTS_ON_EPD = 'agentsOnEpd',
  EPD_FORMATS = 'epdFormats',
  STATUSES = 'statuses',
  COMPANY_ROLES = 'companyRoles',
  PCRS = 'pcrs',
  CPCRS = 'c-pcrs',
  TOOL_FILE_TYPES = 'toolFileTypes',
  TOOL_OWNERS = 'toolOwners',
}

export type AdminSearchEpdProps = {
  registrationNumber: string | null;
  title: string | null;
  companyId: string | null;
  licenseeId: string | null;
  agentId: string | null;
  epdFormat: string | null;
  status: EPDStatusDisplayName | null;
  excludeCopyGroupEpds: boolean;
  excludeEPDsWithoutRegistrationDate: boolean;
};

export type AdminSearchEpdRequest = AdminSearchEpdProps & {
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortDirection: 'asc' | 'desc' | null;
};

export interface AdminEpdSearchResult {
  id: string;
  epdId: string;
  epdVersionId: string;
  registrationNumber: string;
  epdName: string;
  epdOwner: string;
  status: EPDStatusDisplayName;
  registrationDate: string | null;
  licensee: string | null;
  agentId: string | null;
  epdFormat: string | null;
  isInGroup: boolean;
}

export type BiogasDataModel = {
  id?: string | null;
  epdVersionId?: string;
  usedInProcessName?: string | null;
  typeOfBiogasMix?: number | null;
  climateImpactIntensity?: number | null;
  ord?: number | null;
};

export type EPDPublicationProcessingTimeframeModel = {
  minProcessingDays: number;
  maxProcessingDays: number;
};
